import Konva from 'konva/src/Core';
import 'konva/src/Shapes/Rect';
import 'konva/src/Animation';
import 'konva/src/DragAndDrop';
import _ from 'common/customLodash';
import helper from 'common/helper';
import EventEmitter from 'common/eventEmitter';
import Design from './design/design';
import hermiteResize from 'common/hermiteResize';
import 'konva/src/filters/RGBA';

class PlacingLayer {
    constructor(viewpointSize, stageSize, colour, editable = true) {
        this.events = new EventEmitter();
        this.designs = [];
        this.colour = colour;
        this.padding = 50;
        this.viewpointSize = viewpointSize;
        this.stageSize = stageSize;
        this.editable = editable;
        this.konvaLayer = new Konva.Layer({
            listening: editable,
            hitGraphEnabled: editable
        });
        this.background = new Konva.Rect(stageSize);
        this.placingArea = helper.getConstrainedSize(viewpointSize, {
            width: stageSize.width - (this.padding * 2),
            height: stageSize.height - (this.padding * 2)
        });
        this.placingArea.x = (stageSize.width / 2) - (this.placingArea.width / 2);
        this.placingArea.y = (stageSize.height / 2) - (this.placingArea.height / 2);
        this.productImage = new Konva.Image();
        this.konvaLayer.add(this.background);
        this.konvaLayer.add(this.productImage);

        if (this.editable) {
            this.konvaLayer.on('mousedown', evt => this.onMouseDown(evt));
        }
    }

    setBackground(backgroundImageObj) {
        const imgSize = helper.getConstrainedSize(backgroundImageObj, this.placingArea);
        const placingAreaCenter = {
            x: this.placingArea.x + (this.placingArea.width / 2),
            y: this.placingArea.y + (this.placingArea.height / 2)
        };
        this.productImage.setWidth(imgSize.width);
        this.productImage.setHeight(imgSize.height);
        this.productImage.setX(placingAreaCenter.x - (imgSize.width / 2));
        this.productImage.setY(placingAreaCenter.y - (imgSize.height / 2));
        this.productImage.image(backgroundImageObj);
        this.productImage.setZIndex(1);
        if (this.colour) {
            this.productImage.cache();
            this.productImage.filters([Konva.Filters.RGBA]);
            this.productImage.alpha(0.5);
            this.productImage.red(parseInt(this.colour.substring(0, 2), 16));
            this.productImage.green(parseInt(this.colour.substring(2, 4), 16));
            this.productImage.blue(parseInt(this.colour.substring(4, 6), 16));
        }
        this.draw();
    }

    addComponent(component) {
        this.konvaLayer.add(component.konvaObject);
        this.draw();
    }

    getDesigns() {
        return this.designs.map(d => {
            const data = {
                id: d.data.id || 0,
                imageUrl: d.data.image,
                instruction: d.getInstruction(),
                reference: d.getReference(),
                data: d.data,
                resizable: d.resizable,
                setResizable: function(resizable) {
                    d.resizable = !!resizable;
                }
            };
            const transformation = this.getDesignPrintTransform(d);
            return Object.assign(data, transformation);
        });
    }

    getDesignPrintTransform(design) {
        const designPos = this.layerToPrintPos(design.getDesignLayerPos());
        const designSize = design.getDesignSize();
        const designRot = design.getDesignRotation();

        return {
            x: designPos.x,
            y: designPos.y,
            width: designSize.width,
            height: designSize.height,
            rotation: designRot,
            zindex: design.getZIndex() - 1
        };
    }

    addDesign(design) {
        this.designs.push(design);
        this.addComponent(design);
        this.resetDesignPlacement(design);
        this.deselectDesigns(this.designs);
        this.draw();

        if (this.editable) {
            design.events.on('delete', evt => this.deleteDesign(design, evt));
            design.events.on('select', evt => this.onDesignSelect(design, evt));
            design.events.on('rotate', evt => this.onDesignRotate(design, evt));
            design.events.on('resize', evt => this.onDesignResize(design, evt));
            design.events.on('dragend', evt => this.onDesignDragEnd(design, evt));
            design.events.on('instruction', evt => this.onInstruction(design, evt));
            design.setDraggable(true);
        }
    }

    deleteDesign(design) {
        _.remove(this.designs, design);
        design.destroy();
        this.draw();
        this.emitChange();
    }

    resetDesignPlacement(design) {
        let layerPos = {
            x: this.konvaLayer.getWidth() / 2 - design.getDesignSize().width / 2,
            y: this.konvaLayer.getHeight() / 2 - design.getDesignSize().height / 2
        };

        if (design.data.x && design.data.y) {
            layerPos = this.printToLayerPos({
                x: design.data.x,
                y: design.data.y
            });
        }

        if (design.data.zindex) {
            design.setZIndex(design.data.zindex);
        }

        design.setDesignLayerPos(layerPos);
        design.setDesignRotation(design.data.rotation || 0);
        design.setDesignSize({
            width: design.data.width,
            height: design.data.height
        });
        this.draw();
    }

    layerToPrintPos(position) {
        return {
            x: position.x - this.placingArea.x,
            y: position.y - this.placingArea.y
        };
    }

    printToLayerPos(position) {
        return {
            x: position.x + this.placingArea.x,
            y: position.y + this.placingArea.y
        };
    }

    onInstruction(design) {
        this.events.emit('instruction', design);
    }

    onMouseDown() {
        this.deselectDesigns(this.designs);
    }

    onDesignSelect(design) {
        var top = this.designs.reduce(function(top, d) {
            return d.getZIndex() > top.getZIndex() ? d : top;
		});

        // Only update if something will change.
		if (design.getZIndex() !== top.getZIndex()) {
			design.moveToTop();
			this.emitChange();
		}

        const unselected = _.filter(this.designs, p => p !== design);
        this.deselectDesigns(unselected);
    }

    onDesignDragEnd(design) {
        this.validatePlacement(design);
        this.emitChange();
    }

    onDesignRotate(design, event) {
        const centerPos = design.getDesignCenterLayerPos();
        const designRot = design.getDesignRotation();
        const startAngle = helper.getAngle(centerPos, event);
        this.konvaLayer.on('mousemove.rotate', evt => {
            const mousePos = {x: evt.evt.layerX, y: evt.evt.layerY};
            const newAngle = helper.getAngle(centerPos, mousePos);
            const angleDiff = newAngle - startAngle;
            design.setDesignRotation(designRot + angleDiff);
            this.draw();
        });
        this.konvaLayer.on('mouseup.rotate', () => {
            this.validatePlacement(design);
            this.emitChange();
            this.konvaLayer.off('mousemove.rotate');
            this.konvaLayer.off('mouseup.rotate');
        });
    }

    onDesignResize(design, event) {
        const startSize = design.getDesignSize();
        const centerPos = design.getDesignCenterLayerPos();
        const startDistance = Math.hypot(event.x - centerPos.x, event.y - centerPos.y);
        this.konvaLayer.on('mousemove', evt => {
            const mousePos = {x: evt.evt.layerX, y: evt.evt.layerY};
            const distance = Math.hypot(mousePos.x - centerPos.x, mousePos.y - centerPos.y);
            const distanceDiff = distance - startDistance;
            const newWidth = startSize.width + distanceDiff;
            const newHeight = newWidth / startSize.width * startSize.height;
            design.setDesignSize({width: newWidth, height: newHeight});
            design.setDesignLayerPos({
                x: centerPos.x - (newWidth / 2),
                y: centerPos.y - (newHeight / 2)
            });
            this.draw();
        });
        this.konvaLayer.on('mouseup', () => {
            this.validatePlacement(design);
            this.emitChange();
            this.konvaLayer.off('mousemove');
            this.konvaLayer.off('mouseup');
        });
    }

    validatePlacement(design) {
        const productCorners = helper.getCorners(this.productImage);
        const designCorners = design.getDesignCorners();
        const isValid = helper.isIntersecting(productCorners, designCorners);

        if (!isValid) {
            this.resetDesignPlacement(design);
        }
    }

    deselectDesigns(designs) {
        designs.forEach(d => d.deselect());
    }

    clear() {
        this.designs.forEach(d => d.destroy());
        this.designs = [];
        this.konvaLayer.draw();
    }

    draw() {
        this.konvaLayer.draw();
    }

    getImageData(maxSize) {
        // Create new canvas
        var c = document.createElement('canvas');
        const stage = new Konva.Stage({
            container: c,
            width: this.placingArea.width,
            height: this.placingArea.height,
            listening: false
        });

        // Create new layer
        const layer = new PlacingLayer(this.viewpointSize, this.stageSize, this.colour, false);
        stage.add(layer.konvaLayer);
        layer.konvaLayer.setOffset(this.placingArea);
        layer.konvaLayer.draw();

        // Add current designs
        this.designs.forEach(d => {
            const currentTransform = this.getDesignPrintTransform(d);
            layer.addDesign(new Design(currentTransform, d.imageObj, false));
        });

        // Set background
        if (this.productImage.image()) {
            layer.setBackground(this.productImage.image());
        }

        // Resize canvas and return dataUrl
        const canvas = layer.konvaLayer.getCanvas()._canvas;
        const size = helper.getConstrainedSize(canvas, maxSize);
        hermiteResize(canvas, size);
        layer.konvaLayer.destroy();
        stage.destroy();
        return canvas.toDataURL();
    }

    emitChange() {
        this.events.emit('change');
    }
}

export default PlacingLayer;



// WEBPACK FOOTER //
// ./src/placingEditor/PlacingLayer.js